import {
  createContext,
  type FC,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

export type BrandTheme = "bni" | "place-africa" | "box-africa";

export interface BrandThemeDetails {
  name: string;
  fullName: string;
  logo: string;
  logoAlt: string;
  logoClassName?: string;
}

export const brandThemes: Record<BrandTheme, BrandThemeDetails> = {
  bni: {
    name: "BNI",
    fullName: "Banque Nationale d'Investissement",
    logo: "/bni_logo.png",
    logoAlt: "Banque Nationale d'Investissement",
  },
  "place-africa": {
    name: "Place Africa",
    fullName: "L'Afrique digitale pour tous",
    logo: "/place-africa-mark.png",
    logoAlt: "Place Africa",
  },
  "box-africa": {
    name: "Box Africa",
    fullName: "Digital solutions for Africa",
    logo: "/logo.png",
    logoAlt: "Box Africa",
    // The supplied asset is light; make it legible on Box Africa's white UI.
    // Extra padding shrinks the mark visually - it renders larger than the other brands' at the same box size.
    logoClassName: "brightness-0 p-3",
  },
};

interface BrandThemeContextValue {
  theme: BrandTheme;
  brand: BrandThemeDetails;
  setTheme: (theme: BrandTheme) => void;
  canSwitchTheme: boolean;
}

const BrandThemeContext = createContext<BrandThemeContextValue>({
  theme: "bni",
  brand: brandThemes.bni,
  setTheme: () => undefined,
  canSwitchTheme: !import.meta.env.PROD,
});

const STORAGE_KEY = "brand-theme";
const productionTheme = import.meta.env.VITE_BRAND_THEME;
const canSwitchTheme = !import.meta.env.PROD;

function isBrandTheme(theme: string | undefined): theme is BrandTheme {
  return theme === "bni" || theme === "place-africa" || theme === "box-africa";
}

function getInitialTheme(): BrandTheme {
  // In production, the brand is part of the generated bundle. Local storage
  // is intentionally ignored so every user sees the deployed identity.
  if (import.meta.env.PROD) {
    return isBrandTheme(productionTheme) ? productionTheme : "bni";
  }

  const storedTheme = localStorage.getItem(STORAGE_KEY) as BrandTheme;
  return isBrandTheme(storedTheme ?? undefined) ? storedTheme : "bni";
}

export const BrandThemeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<BrandTheme>(getInitialTheme);

  const updateTheme = (nextTheme: BrandTheme) => {
    if (canSwitchTheme) {
      setTheme(nextTheme);
    }
  };

  useEffect(() => {
    document.documentElement.dataset.brand = theme;
    document.documentElement.style.colorScheme = "light";
    localStorage.setItem(STORAGE_KEY, theme);
    document.title = `${brandThemes[theme].name} | Référentiel Identité`;
  }, [theme]);

  return (
    <BrandThemeContext.Provider
      value={{
        theme,
        brand: brandThemes[theme],
        setTheme: updateTheme,
        canSwitchTheme,
      }}
    >
      {children}
    </BrandThemeContext.Provider>
  );
};

export const useBrandTheme = () => useContext(BrandThemeContext);
